'use client';

import React, { useState, useRef, useCallback } from 'react';
import { Send, Paperclip, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { DirectMessage } from '@/types/chat';

const MAX_MESSAGE_LENGTH = 2000;
const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/webm'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export interface MessageInputProps {
  onSendMessage: (
    content: string,
    attachmentUrl?: string,
    attachmentType?: DirectMessage['attachment_type']
  ) => Promise<{ success: boolean; error?: string }>;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  onAttachmentUpload?: (file: File) => Promise<{ url: string; type: DirectMessage['attachment_type'] } | null>;
}

interface AttachmentPreview {
  file: File;
  previewUrl: string;
  type: DirectMessage['attachment_type'];
}

export function MessageInput({
  onSendMessage,
  disabled = false,
  placeholder = 'Type a message...',
  className,
  onAttachmentUpload,
}: MessageInputProps) {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachment, setAttachment] = useState<AttachmentPreview | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const characterCount = message.length;
  const isOverLimit = characterCount > MAX_MESSAGE_LENGTH;
  const charactersRemaining = MAX_MESSAGE_LENGTH - characterCount;
  const showCharacterWarning = characterCount >= MAX_MESSAGE_LENGTH * 0.9;

  const canSend = !disabled && !isSending && !isUploading && (message.trim() || attachment) && !isOverLimit;

  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    }
  }, []);

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    setError(null);
    adjustTextareaHeight();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Validate file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setError('File type not supported. Please use JPEG, PNG, GIF, WebP images or MP4, WebM videos.');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError('File is too large. Maximum size is 10MB.');
      return;
    }

    // Determine attachment type
    const attachmentType: DirectMessage['attachment_type'] = file.type.startsWith('video/')
      ? 'video'
      : 'image';

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);

    setAttachment({
      file,
      previewUrl,
      type: attachmentType,
    });
    setError(null);
  };

  const removeAttachment = () => {
    if (attachment) {
      URL.revokeObjectURL(attachment.previewUrl);
      setAttachment(null);
    }
  };

  const handleSubmit = async () => {
    if (!canSend) return;

    const trimmedMessage = message.trim();
    if (!trimmedMessage && !attachment) return;

    setIsSending(true);
    setError(null);

    try {
      let uploadedUrl: string | undefined;
      let uploadedType: DirectMessage['attachment_type'] | undefined;

      // Upload attachment if present
      if (attachment && onAttachmentUpload) {
        setIsUploading(true);
        const result = await onAttachmentUpload(attachment.file);
        setIsUploading(false);

        if (!result) {
          setError('Failed to upload attachment');
          setIsSending(false);
          return;
        }

        uploadedUrl = result.url;
        uploadedType = result.type;
      }

      const result = await onSendMessage(trimmedMessage, uploadedUrl, uploadedType);

      if (result.success) {
        setMessage('');
        removeAttachment();
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }
      } else {
        setError(result.error || 'Failed to send message');
      }
    } catch (err) {
      console.error('[MessageInput] Send error:', err);
      setError('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div
      className={cn(
        'border-t border-[color:var(--jr-border)] bg-[color:rgba(24,15,15,0.96)] p-4 backdrop-blur-xl',
        className
      )}
      data-testid="message-input"
    >
      {/* Error display */}
      {error && (
        <div
          className="jr-status-error mb-2"
          data-testid="message-input-error"
        >
          {error}
        </div>
      )}

      {/* Attachment preview */}
      {attachment && (
        <div
          className="mb-3 relative inline-block"
          data-testid="attachment-preview"
        >
          <div className="relative overflow-hidden rounded-[20px] border border-[color:var(--jr-border)] bg-[color:rgba(49,31,29,0.9)]">
            {attachment.type === 'image' ? (
              <img
                src={attachment.previewUrl}
                alt="Attachment preview"
                className="max-h-32 max-w-48 object-cover"
              />
            ) : (
              <video
                src={attachment.previewUrl}
                className="max-h-32 max-w-48 object-cover"
              />
            )}
            <button
              type="button"
              onClick={removeAttachment}
              className="absolute -top-2 -right-2 rounded-full border border-[color:rgba(183,90,86,0.35)] bg-[color:rgba(110,43,47,0.92)] p-1 text-[color:#efc2bd] shadow-md transition-colors hover:bg-[color:rgba(130,49,54,0.98)]"
              aria-label="Remove attachment"
              data-testid="remove-attachment-button"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}

      <div className="flex items-end gap-2">
        {/* Attachment button */}
        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_FILE_TYPES.join(',')}
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled || isSending}
          data-testid="attachment-file-input"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isSending || !!attachment}
          className="h-10 w-10 shrink-0 rounded-2xl border border-[color:var(--jr-border)] bg-[color:rgba(49,31,29,0.9)] text-[color:var(--jr-text-soft)] hover:bg-[color:rgba(66,42,38,0.96)] hover:text-[color:var(--jr-text)]"
          aria-label="Attach file"
          data-testid="attachment-button"
        >
          {isUploading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : attachment ? (
            <ImageIcon className="h-5 w-5 text-primary" />
          ) : (
            <Paperclip className="h-5 w-5" />
          )}
        </Button>

        {/* Text input */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleMessageChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled || isSending}
            rows={1}
            className={cn(
              'jr-input min-h-[42px] w-full resize-none rounded-[20px] py-2.5 pr-14 text-sm',
              'placeholder:text-[color:var(--jr-text-muted)]',
              'focus:outline-none focus:ring-2 focus:ring-[color:rgba(214,168,139,0.18)] focus:ring-offset-0',
              'disabled:cursor-not-allowed disabled:opacity-50',
              'min-h-[42px] max-h-[150px]',
              isOverLimit && 'border-[color:rgba(183,90,86,0.55)] focus:ring-[color:rgba(183,90,86,0.3)]'
            )}
            data-testid="message-input-textarea"
          />

          {/* Character count indicator */}
          {showCharacterWarning && (
            <div
              className={cn(
                'absolute right-3 bottom-2 text-xs',
                isOverLimit ? 'font-medium text-[color:#efc2bd]' : 'text-[color:var(--jr-text-muted)]'
              )}
              data-testid="character-count"
            >
              {charactersRemaining}
            </div>
          )}
        </div>

        {/* Send button */}
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={!canSend}
          className="jr-action-primary h-10 shrink-0 rounded-2xl px-3"
          aria-label="Send message"
          data-testid="send-button"
        >
          {isSending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
